import { AbstractStore, memone } from '@deqo/foundation';
import { injectable } from 'inversify';
import { easeOutQuint } from 'just-curves';
import { Animation, animate } from '../../CodesThatsGottaGoSomewhereElse';
import { round } from '../../platform/geom/Math';
import { Matrix, MatrixTransformable } from '../../platform/geom/Matrix';
import { Point, PointSource } from '../../platform/geom/Point';
import { Rect, RectLike } from '../../platform/geom/Rect';

export interface Viewport {
    width: number;
    height: number;
}

export type CameraState = {
    ready: boolean,
    offset: Point,
    position: Point,
    scale: number,
    width: number,
    height: number,
};

@injectable()
export class Camera extends AbstractStore<CameraState> {

    private anim: Animation | null = null;

    protected state = {
        ready: false,
        offset: Point.zero,
        position: Point.zero,
        scale: 1,
        width: 640,
        height: 480,
    };

    public get calibrated(): boolean {
        return this.state.ready;
    }

    public get position(): Point {
        return this.state.position;
    }

    public get scale(): number {
        return this.state.scale;
    }

    public get viewport(): Viewport {
        return this.state;
    }

    public get viewModelMatrix(): Matrix {
        return this.computeViewModelMatrix(this.state);
    }

    public get modelViewMatrix(): Matrix {
        return this.computeModelViewMatrix(this.state);
    }

    public getViewFrame(): Rect {
        return new Rect(0, 0, this.state.width, this.state.height);
        // const wh = [this.state.width / 2, this.state.height / 2];
        // return Rect.fromPoints([
        //     this.position.subtract(wh),
        //     this.position.add(wh),
        // ]);
    }

    public getModelFrame(): Rect {
        const wh = [this.state.width / this.scale / 2, this.state.height / this.scale / 2];
        return Rect.fromPoints([
            this.position.subtract(wh),
            this.position.add(wh),
        ]);
    }

    public toEdge(viewPt: Point) {
        const area = this.getViewFrame().inflate(-50);
        if (viewPt.x > area.right) {
            if (viewPt.y < area.top) return 'ne';
            if (viewPt.y > area.bottom) return 'se';
            return 'e';
        }
        if (viewPt.x < area.left) {
            if (viewPt.y < area.top) return 'nw';
            if (viewPt.y > area.bottom) return 'sw';
            return 'w';
        }
        if (viewPt.y < area.top) return 'n';
        if (viewPt.y > area.bottom) return 's';
        return 'none';
    }

    public toEdgeVector(viewPt: Point): Point {
        switch (this.toEdge(viewPt)) {
            case 'e': return new Point(1, 0);
            case 'se': return new Point(1, 1).normalize();
            case 's': return new Point(0, 1);
            case 'sw': return new Point(-1, 1).normalize();
            case 'w': return new Point(-1, 0);
            case 'nw': return new Point(-1, -1).normalize();
            case 'n': return new Point(0, -1);
            case 'ne': return new Point(1, -1).normalize();
            default: return Point.zero;
        }
    }

    public toModelPoint(viewPt: Point) {
        return this.viewModelMatrix.apply(viewPt);
    }

    public toModel(vpt: Point): Point;
    public toModel(vpts: readonly Point[]): Point[];
    public toModel<T>(vg: MatrixTransformable<T>): T;
    public toModel(input: any): any {
        return this.viewModelMatrix.apply(input);
    }

    public toViewFromScreen(vpt: Point): Point;
    public toViewFromScreen(vpts: readonly Point[]): Point[];
    public toViewFromScreen<T>(vg: MatrixTransformable<T>): T;
    public toViewFromScreen(input: any): any {
        return Matrix.translate(this.state.offset.multiply(-1)).apply(input);
    }

    public toViewPoint(modelPt: Point) {
        return this.modelViewMatrix.apply(modelPt);
    }

    public toView(mpt: Point): Point;
    public toView(mpts: readonly Point[]): Point[];
    public toView<T>(mg: MatrixTransformable<T>): T;
    public toView(input: any): any {
        return this.modelViewMatrix.apply(input);
    }

    public toScreenFromView(vpt: Point): Point {
        return vpt.add(this.state.offset);
    }

    public calibrate(viewport: HTMLElement): void {
        this.updateState(next => {
            next.ready = true;
            next.offset = Point.from(viewport.getBoundingClientRect());
            next.width = viewport.clientWidth;
            next.height = viewport.clientHeight;
        });
    }

    public waitForCalibrated() {
        if (this.calibrated) return Promise.resolve();
        return new Promise<void>(rs => {
            const unsub = this.observe(ns => {
                if (ns.ready) {
                    unsub();
                    rs();
                }
            });
        });
    }

    public move(dist: PointSource) {
        this.moveTo(this.position.add(Point.from(dist).divide(this.scale)));
    }

    public moveTo(pt: PointSource): any {
        this.setView(Point.from(pt), this.scale);
    }

    public zoom(amount: number) {
        this.zoomTo(clamp(this.scale + (amount / 10)));
    }

    public zoomTo(value: number) {
        if (value < 0.05) {
            value = 0.05;
        }
        if (value > 4) {
            value = 4;
        }
        this.setView(this.position, value);
    }

    public setView(position: PointSource, scale: number) {
        this.updateState(next => {
            next.position = Point.from(position);
            next.scale = round(scale, 6);
        });
    }

    public setFrame(rect: Rect, padFactor = 0.9): void {
        this.setView(
            rect.center(),
            Math.min(
                (this.state.width * padFactor) / rect.width,
                (this.state.height * padFactor) / rect.height
            )
        );
    }

    public async animateTo(position: Point, scale?: number, duration?: number, easing?: (t: number) => number): Promise<void> {
        duration = duration || Math.max(1000, Math.min(200, Point.vector(this.state.position, position).length() / 10));
        easing = easing || easeOutQuint;

        const sp = this.position;
        const ss = this.scale;
        const tp = position;
        const ts = scale ?? ss;
        if (sp.equals(tp) && ss === ts) return;

        const fx = (t => {
            t = easing(t);
            this.setView(
                sp.add(tp.subtract(sp).multiply(t)),
                ss + ((ts - ss) * t)
            );
        });

        if (this.anim) {
            this.anim.stop();
        }

        this.anim = animate(fx, duration);
        await this.anim.play();
    }

    public async animateToFrame(rect: RectLike, duration?: number, easing?: (t: number) => number): Promise<void> {

        const position = Rect.prototype.center.call(rect);
        const scale = Math.min(1.5, Math.min(
            // (this.viewport.width - 160) / rect.width,
            // (this.viewport.height - 120) / rect.height
            (this.viewport.width) / rect.width,
            (this.viewport.height) / rect.height
        ));

        await this.animateTo(position, scale, duration, easing);
    }

    @memone()
    private computeModelViewMatrix(state: CameraState): Matrix {

        const containerOffset = new Point(
            Math.floor(state.width / 2),
            Math.floor(state.height / 2)
        );

        return Matrix.identity
            // .rotate(toRadians(10))
            .translate(containerOffset)
            .scale(state.scale)
            .translate(state.position.multiply(-1));
    }

    @memone()
    private computeViewModelMatrix(state: CameraState): Matrix {
        return this.computeModelViewMatrix(state).inverse();
    }
}

function clamp(value: number): number {
    return Math.round(value * 100) / 100;
}
